import { Box, Button, Group, Loader, Paper, Stack, Tabs, Title } from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { IconPlus } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { zodResolver } from "mantine-form-zod-resolver";
import { Suspense } from "react";
import { AppError } from "shared/components/global/AppError/AppError";
import { Content } from "shared/components/global/Content";
import { AssignShapesModal } from "shared/components/organization/geolocations/AssignShapesModal";
import {
    createGeoLocationsFormState,
    GeoLocationsFormProvider,
    GeoLocationsFormSchema,
} from "shared/components/organization/geolocations/context";
import { GeoLocationsMap } from "shared/components/organization/geolocations/GeoLocationsMap";
import { LayersPanel } from "shared/components/organization/geolocations/LayersPanel";
import { useGeoLocations } from "shared/components/organization/geolocations/useGeoLocations";
import { useIsMobile } from "shared/hooks/useIsMobile";

const Header = () => {
    const isMobile = useIsMobile();
    const geo = useGeoLocations();

    function handleAssignShapes() {
        modals.open({
            title: "Assign Shapes",
            size: "lg",
            children: (
                <Suspense fallback={<Loader />}>
                    <AssignShapesModal onFetchLayerShapes={geo.handleFetchLayerShapes} />
                </Suspense>
            ),
        });
    }

    return (
        <Group justify="space-between" align="center" px={isMobile ? "sm" : 0} style={{ gap: isMobile ? 0 : "sm" }}>
            <Content.Heading>
                <Title order={3} size="h1">
                    Geo-Locations
                </Title>
            </Content.Heading>
            <Content.Action>
                <Group gap="xs" mb={isMobile ? "xs" : 0} style={{ width: isMobile ? "100%" : "auto" }} justify="flex-end">
                    <Button variant="default" size={isMobile ? "compact-md" : "md"} onClick={handleAssignShapes}>
                        Assign Shapes
                    </Button>
                    <Button
                        leftSection={<IconPlus size={16} />}
                        size={isMobile ? "compact-md" : "md"}
                        onClick={geo.handleGlobalAddShape}
                        disabled={geo.layers.length === 0 || !!geo.drawingLayerId}
                    >
                        Add Shape
                    </Button>
                </Group>
            </Content.Action>
        </Group>
    );
};

const component = () => {
    const isMobile = useIsMobile();
    const form = useForm({
        initialValues: createGeoLocationsFormState(),
        validate: zodResolver(GeoLocationsFormSchema),
    });

    return (
        <GeoLocationsFormProvider form={form}>
            {isMobile ? (
                <MobileLayout />
            ) : (
                <Content>
                    <Stack h="90dvh" gap={0}>
                        <Header />
                        <Paper radius="md" withBorder style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                            <Group gap={0} align="stretch" wrap="nowrap" style={{ flex: 1, minHeight: 0 }}>
                                <GeoLocationsMap />
                                <LayersPanel />
                            </Group>
                        </Paper>
                    </Stack>
                </Content>
            )}
        </GeoLocationsFormProvider>
    );
};

const MobileLayout = () => {
    const geo = useGeoLocations();

    if (geo.drawingLayerId) {
        return (
            <Stack pos="relative" h="100dvh" gap={0}>
                <Header />
                <Box style={{ flex: 1, position: "relative", overflow: "hidden" }}>
                    <GeoLocationsMap />
                    <Box
                        pos="absolute"
                        bottom={0}
                        left={0}
                        right={0}
                        mah="50%"
                        style={{
                            zIndex: 10,
                            overflowY: "auto",
                            borderTop: "1px solid var(--app-shell-border-color)",
                        }}
                    >
                        <LayersPanel />
                    </Box>
                </Box>
            </Stack>
        );
    }

    return (
        <Stack pos="relative" h="100dvh" gap={0}>
            <Header />
            <Tabs defaultValue="map" style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <Tabs.List grow>
                    <Tabs.Tab value="map">Map</Tabs.Tab>
                    <Tabs.Tab value="layers">Layers</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="map" style={{ flex: 1, position: "relative" }}>
                    <GeoLocationsMap />
                </Tabs.Panel>
                <Tabs.Panel value="layers" style={{ flex: 1, overflow: "hidden" }}>
                    <LayersPanel />
                </Tabs.Panel>
            </Tabs>
        </Stack>
    );
};

/** @public */
export const Route = createFileRoute("/_auth/organization/geolocations/")({
    component,
    errorComponent: AppError,
});
